import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AssetForm, { labelsFor } from '../components/AssetForm';
import AssetImport from '../components/AssetImport';
import CompanyChip from '../components/asset-company/CompanyChip';
import HolderSelect from '../components/HolderSelect';
import PersonalMarks from '../components/asset/PersonalMarks';
import CompanyFilter from '../components/asset-company/CompanyFilter';
import { contractorAuth } from '../lib/contractorAuth';
import { useFeature } from '../lib/features';
import {
  useAssetMeta, refreshAssetMeta, deviceName, warrantyInWords, downloadFile,
  rememberedCompanies, rememberCompanies,
  LIFECYCLE_LABEL, lifecycleTone,
  holderLabel, tagText
} from '../lib/assets';
import '../components/Assets.css';
import { cameFrom } from '../components/asset/BackLink';

/**
 * The register: every device this person may see.
 *
 * The SERVER filters the rows - a person with a grant on the monitors in
 * Valletta gets exactly those and nothing else - so this page draws what
 * it is given and never hides a row itself. Since 062 that person may be a
 * contractor on their portal token; contractorAuth() signs their calls and
 * is nothing for a member of staff.
 *
 * The filters live in the address, so a filtered list can be bookmarked or
 * pasted to a colleague, and the back button undoes a filter rather than
 * leaving the page. The COMPANY filter is also remembered per browser: IT
 * looking after one company's devices should not re-tick it every visit,
 * so a visit with no company in the address starts from the last choice.
 *
 * THE LAYOUT (tidied 28 October 2026, Gordon: "quite disorganised"):
 *
 *   1. One header: the title, "Add a device" (Import under its small menu)
 *      and one row of links to the rest of the Asset Portal.
 *   2. One filter bar: search, Category, Status, Who has it, Place, and
 *      "More filters" for the rest, with a count of the ones in use there,
 *      and "Clear filters".
 *   3. Bulk actions only on ticking: tick rows and a bar appears with Print
 *      labels and the other bulk actions. The header box ticks the page; the
 *      bar then offers every row the filters match (Gmail's way), which
 *      Print labels can honour (the label page's filter mode, at most
 *      LABEL_CAP in one print). Move, default office and company take a
 *      list of ids on the server, so they stay with the ticked rows and the
 *      bar says so.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PAGE = 50;
// The most labels the server makes in one print (routes/asset-entry.js).
const LABEL_CAP = 1000;

const STAGES = ['deployed', 'in_stock', 'in_repair', 'ordered', 'received'];

// Every filter the address may carry; "Clear filters" takes them all off.
const FILTER_KEYS = ['q', 'category', 'status', 'holder', 'location', 'company', 'no_location', 'reporting',
  'warranty', 'away', 'no_default', 'label', 'personal'];

/** The small menu under "Add a device" (Import), in AssetLayout's menu style. */
function AddMenu({ items }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const list = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    list.current?.querySelector('[role="menuitem"]')?.focus();
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  const onKey = (e) => {
    const els = [...(list.current?.querySelectorAll('[role="menuitem"]') || [])];
    const at = els.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); wrap.current?.querySelector('.as-reg-addmore')?.focus(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); els[(at + 1) % els.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); els[(at - 1 + els.length) % els.length]?.focus(); }
    if (e.key === 'Tab') setOpen(false);
  };
  return (
    <div className="as-menu-wrap" ref={wrap}>
      <button type="button" className="btn btn-primary as-reg-addmore" aria-haspopup="menu" aria-expanded={open}
        aria-label="Other ways to add" onClick={() => setOpen(!open)}>
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" fill="currentColor"><path d="M4 6l4 4 4-4z" /></svg>
      </button>
      {open && (
        <>
          <div className="as-sheet-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
          <ul className="as-menu" role="menu" ref={list} onKeyDown={onKey}>
            {items.map((it) => (
              <li key={it.key} role="none">
                <button type="button" role="menuitem" className="as-menu-item"
                  onClick={() => { setOpen(false); it.run(); }}>
                  <span>{it.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function AssetsPage() {
  const navigate = useNavigate();
  const meta = useAssetMeta();
  const licencesOn = useFeature('licences');
  const [params, setParams] = useSearchParams();
  // The address as it is this moment. The router's copy (params) is updated
  // in a transition, so a click just made may not be in it yet; a write built
  // on it - the search written a moment after typing, above all - put back a
  // filter just switched off. Reading is from params; writing starts here.
  const liveParams = () => new URLSearchParams(window.location.search);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  // Ticked rows, for every bulk action. Kept across pages of the list on
  // purpose: IT ticks a few here and a few on the next page and deals with
  // them together.
  const [picked, setPicked] = useState(() => new Set());
  // "Select all M matching": every row the filters match, not only those
  // ticked. Only Print labels can act on that (filter mode on the server).
  const [allMatching, setAllMatching] = useState(false);
  const toggle = (id) => {
    setAllMatching(false);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => { setPicked(new Set()); setAllMatching(false); };
  const [query, setQuery] = useState(params.get('q') || '');
  // How many things wait in the Discovery inbox (092): shown on the link
  // for whoever may open it; zero, and no link, for everybody else.
  const [waiting, setWaiting] = useState(null);
  const [bulkCompany, setBulkCompany] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  // Default office (104): a place for the ticked rows, or 'current' for each
  // one's own place.
  const [bulkOffice, setBulkOffice] = useState('');
  // Move to… (a place for the ticked rows).
  const [bulkPlace, setBulkPlace] = useState('');

  const contractor = Boolean(contractorAuth());
  const status = params.get('status') || '';
  const personalOnly = params.get('personal') === '1';
  const offset = Number(params.get('offset') || 0);
  const companyIds = (params.get('company') || '').split(',').map((s) => s.trim()).filter(Boolean);
  const hasCompanies = Array.isArray(meta?.companies);

  // The filters that live under "More filters", and how many are in use.
  const hiddenInUse = [
    hasCompanies && companyIds.length > 0,
    params.get('reporting'), params.get('warranty'),
    params.get('away') || params.get('no_default'),
    params.get('label'), params.get('no_location') === '1', personalOnly
  ].filter(Boolean).length;
  const [moreOpen, setMoreOpen] = useState(() => hiddenInUse > 0);
  const anyFilter = FILTER_KEYS.some((k) => params.get(k));

  const setParam = (k, v) => {
    const next = liveParams();
    if (v) next.set(k, v); else next.delete(k);
    if (k !== 'offset') next.delete('offset');
    setParams(next);
  };

  // A new filter is a new "everything matching": the Gmail-style choice
  // goes, the ticked rows stay.
  const filterKey = (() => {
    const q = new URLSearchParams(params);
    q.delete('offset');
    return q.toString();
  })();
  useEffect(() => { setAllMatching(false); }, [filterKey]);

  // The remembered company choice, applied once when the address says
  // nothing about companies - and only once the register has companies,
  // so a stale memory on a database without 062 asks nothing of the server.
  const [recalled, setRecalled] = useState(false);
  useEffect(() => {
    if (recalled || !hasCompanies) return;
    setRecalled(true);
    if (params.has('company')) return;
    const known = new Set([...(meta.companies || []).map((c) => c.id), 'none']);
    const wanted = rememberedCompanies().filter((id) => known.has(id));
    if (wanted.length) setParam('company', wanted.join(','));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCompanies, recalled]);

  const chooseCompanies = (ids) => {
    rememberCompanies(ids);
    setParam('company', ids.join(','));
  };

  const clearFilters = () => {
    const next = liveParams();
    FILTER_KEYS.forEach((k) => next.delete(k));
    next.delete('offset');
    if (companyIds.length) rememberCompanies([]);
    setQuery('');
    setParams(next);
  };

  // Only the latest request's answer is shown: an older, slower one arriving
  // last would otherwise put the rows of a filter already changed back.
  const latest = useRef(0);
  const load = useCallback(() => {
    const q = new URLSearchParams(params);
    q.set('limit', String(PAGE));
    const n = ++latest.current;
    axios.get(`${API_BASE}/assets?${q.toString()}`, contractorAuth())
      .then((r) => { if (n === latest.current) { setData(r.data); setError(''); } })
      .catch((e) => { if (n === latest.current) setError(e.response?.data?.error || 'Could not load the register.'); });
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const mayDiscover = meta?.access?.all === 'write' && !contractor;
  useEffect(() => {
    if (!mayDiscover) { setWaiting(null); return undefined; }
    let live = true;
    axios.get(`${API_BASE}/assets/discoveries/counts`)
      .then((r) => { if (live) setWaiting(r.data?.hidden ? null : r.data); })
      .catch(() => { if (live) setWaiting(null); });
    return () => { live = false; };
  }, [mayDiscover]);

  // Typing filters after a pause, rather than one request per key.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((liveParams().get('q') || '') !== query.trim()) setParam('q', query.trim());
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  if (meta?.missing) {
    return (
      <div className="container">
        <div className="page-header"><h1 className="page-title">Assets</h1></div>
        <p className="as-note" data-tone="warning">{meta.error}</p>
      </div>
    );
  }

  const counts = data?.counts || {};
  const total = data?.total || 0;
  const rows = data?.assets || [];
  const everything = STAGES.reduce((n, s) => n + (counts[s] || 0), 0);
  const canAdd = Boolean(meta?.access?.write);
  // Importing writes anywhere in the register, so the server asks for write
  // on all of it; offering it to anybody else would be a 403.
  const canImport = meta?.access?.all === 'write' && !contractor;
  const exportList = () => {
    const q = new URLSearchParams(params);
    q.delete('offset');
    q.set('format', 'xlsx');
    downloadFile(`/assets?${q}`, 'jarvis-devices.xlsx')
      .catch(() => setError('Could not export the list.'));
  };

  // The ticking: the header box ticks (or unticks) this page's rows.
  const pageIds = rows.map((a) => a.id);
  const pageTicked = pageIds.filter((id) => picked.has(id)).length;
  const wholePage = pageIds.length > 0 && pageTicked === pageIds.length;
  const tickPage = () => {
    setAllMatching(false);
    setPicked((prev) => {
      const next = new Set(prev);
      if (wholePage) pageIds.forEach((id) => next.delete(id)); else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  // Some of the page ticked: the header box shows a dash.
  const headBox = (el) => { if (el) el.indeterminate = pageTicked > 0 && !wholePage; };
  // Printing every match goes through the label page's filter mode; a
  // personal item has no register label, so that list does not offer it.
  const mayPrint = canAdd && !contractor;
  const offerAll = mayPrint && wholePage && total > pageIds.length && !allMatching && !personalOnly;
  const chosen = allMatching ? total : picked.size;

  // Print labels: the ticked rows by id, or everything matching by filter.
  // The server holds at most LABEL_CAP in one print; over 100 asks first.
  const printLabels = () => {
    if (!allMatching) {
      navigate(`/assets/labels?ids=${[...picked].join(',')}`);
      return;
    }
    if (total > 100) {
      const note = total > LABEL_CAP
        ? ` A print holds at most ${LABEL_CAP} labels, so this prints the first ${LABEL_CAP} - print again afterwards with "Label not printed yet" for the rest.`
        : '';
      if (!window.confirm(`Print labels for all ${total} matching devices?${note}`)) return;
    }
    const q = new URLSearchParams(params);
    q.delete('offset'); q.delete('limit'); q.delete('format'); q.delete('personal');
    q.set('filter', '1');
    navigate(`/assets/labels?${q}`);
  };

  const setCompanyOnPicked = async () => {
    setBulkNote('');
    setError('');
    try {
      const r = await axios.post(`${API_BASE}/assets/company`,
        { ids: [...picked], company_id: bulkCompany === 'none' ? null : bulkCompany }, contractorAuth());
      const { changed, skipped } = r.data;
      const to = r.data.company ? r.data.company.name : 'no company';
      setBulkNote(`${changed} device${changed === 1 ? '' : 's'} now belong${changed === 1 ? 's' : ''} to ${to}`
        + (skipped ? `; ${skipped} you may not change ${skipped === 1 ? 'was' : 'were'} left alone.` : '.'));
      clearSelection();
      setBulkCompany('');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not set the company.');
    }
  };

  const setOfficeOnPicked = async () => {
    setBulkNote('');
    setError('');
    try {
      const body = { asset_ids: [...picked],
        location_id: bulkOffice === 'current' ? null : bulkOffice, from_current: bulkOffice === 'current' };
      const r = await axios.post(`${API_BASE}/assets/bulk-default`, body, contractorAuth());
      const done = (r.data?.done || []).length;
      const refused = (r.data?.refused || []).length;
      setBulkNote(`${done} device${done === 1 ? '' : 's'} now have a default office`
        + (refused ? `; ${refused} could not be changed.` : '.'));
      clearSelection();
      setBulkOffice('');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not set the default office.');
    }
  };

  const movePicked = async () => {
    setBulkNote('');
    setError('');
    try {
      const r = await axios.post(`${API_BASE}/assets/bulk-move`,
        { asset_ids: [...picked], location_id: bulkPlace }, contractorAuth());
      const done = (r.data?.done || []).length;
      const refused = r.data?.refused || [];
      setBulkNote(`${done} device${done === 1 ? '' : 's'} moved`
        + (refused.length ? `; ${refused.length} could not be moved: ${refused.map((x) => `${x.asset_tag || x.id} (${x.reason})`).join(', ')}.` : '.'));
      clearSelection();
      setBulkPlace('');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not move the devices.');
    }
  };

  const companyOf = (id) => (meta?.companies || []).find((c) => c.id === id);
  const liveCompanies = (meta?.companies || []).filter((c) => !c.archived_at);
  const activePlaces = (meta?.locations || []).filter((l) => l.active);

  // The rest of the Asset Portal, as one row of links. Contractors see the
  // register only; the other screens are staff's.
  const links = contractor ? [] : [
    { to: '/assets/lookup', label: 'Look up' },
    { to: '/assets/map', label: 'Map' },
    ...(meta?.access?.any ? [{ to: '/assets/devices', label: 'Devices' }, { to: '/assets/devices/fleet', label: 'Fleet health' }] : []),
    ...(waiting ? [{ to: '/assets/discovery', label: 'Discovery', count: waiting.total > 0 ? `${waiting.total} waiting` : null }] : []),
    // 108: the leavers whose desks and devices are sorted out by hand.
    ...(meta?.desks && meta?.access?.write ? [{ to: '/assets/leavers', label: 'Leavers' }] : []),
    // 119: the licences register, an administrator's (and behind its switch).
    ...(meta?.access?.admin && licencesOn ? [{ to: '/assets/licences', label: 'Licences' }] : [])
  ];

  const selectionWords = allMatching
    ? `All ${total} matching selected`
    : `${picked.size} selected`;

  return (
    <div className="container">
      <div className="page-header as-reg-head">
        <div className="as-reg-heading">
          <h1 className="page-title">Assets</h1>
          <p className="page-description">
            Every device the firm owns that you may see: who has it, where it is, and whether it is
            still under warranty.
          </p>
        </div>
        {canAdd && (
          <div className="as-reg-add">
            <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
              Add a device
            </button>
            {canImport && (
              <AddMenu items={[{ key: 'import', label: 'Import from a spreadsheet', run: () => setImporting(true) }]} />
            )}
          </div>
        )}
        {links.length > 0 && (
          <nav className="as-reg-links" aria-label="Asset Portal">
            <ul>
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to}>
                    {l.label}
                    {l.count && <span className="as-reg-count">{l.count}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>

      {meta && !meta.enabled && meta.access?.admin && (
        <p className="as-note">
          The register is switched off, so only administrators see it. Enter devices first, then
          switch it on in Settings → Assets & devices → Register — that is when staff see the Device choice on Create
          Ticket.
        </p>
      )}

      <div className="as-reg-filters" role="search" aria-label="Filter the register">
        <div className="as-toolbar">
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={hasCompanies ? 'Tag, serial, model, person or company' : 'Tag, serial, model or person'}
            aria-label="Search the register" />
          <select value={params.get('category') || ''} aria-label="Category"
            onChange={(e) => setParam('category', e.target.value)}>
            <option value="">Every category</option>
            {(meta?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {/* Status: the lifecycle stages with their counts. Personal items
              (105) are their own list, under More filters. */}
          <select value={status} aria-label="Status"
            onChange={(e) => {
              const next = liveParams();
              next.delete('personal'); next.delete('offset');
              if (e.target.value) next.set('status', e.target.value); else next.delete('status');
              setParams(next);
            }}>
            <option value="">{`All in service (${everything})`}</option>
            {STAGES.filter((s) => counts[s] || status === s).map((s) => (
              <option key={s} value={s}>{`${LIFECYCLE_LABEL[s]} (${counts[s] || 0})`}</option>
            ))}
            <option value="retired">Retired</option>
            {status && status !== 'retired' && !STAGES.includes(status) && (
              <option value={status}>{LIFECYCLE_LABEL[status] || status}</option>
            )}
          </select>
          {/* Who has it (093): a person or a contractor, by the same keys the
              server reads - 'u:<id>', 'c:<id>' - and 'none' for nobody. A
              contractor's card in Settings links here with their key, so a
              key that is not in the list is still shown as what it is. */}
          <HolderSelect value={params.get('holder') || ''} aria-label="Who has it" meta={meta}
            none="Anybody" also={[{ key: 'none', label: 'Nobody' }, ...(meta?.desks ? [{ key: 'desks', label: 'A desk (its kit)' }] : [])]}
            onChange={(key) => setParam('holder', key)} />
          <select value={params.get('location') || ''} aria-label="Place"
            onChange={(e) => setParam('location', e.target.value)}>
            <option value="">Everywhere</option>
            {(meta?.locations || []).map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
          </select>
          <button type="button" className="btn btn-plain btn-small as-reg-more" aria-expanded={moreOpen}
            aria-controls="as-more-filters" onClick={() => setMoreOpen(!moreOpen)}>
            More filters
            {hiddenInUse > 0 && (
              <span className="as-reg-badge" aria-label={`${hiddenInUse} in use`} data-testid="more-filters-count">{hiddenInUse}</span>
            )}
          </button>
          {anyFilter && (
            <button type="button" className="btn btn-plain btn-small" onClick={clearFilters}>Clear filters</button>
          )}
        </div>

        {moreOpen && (
          <div className="as-toolbar as-more-filters" id="as-more-filters" data-testid="more-filters">
            {hasCompanies && (
              <CompanyFilter companies={meta.companies} value={companyIds} counts={data?.by_company || null}
                onChange={chooseCompanies} />
            )}
            <select value={params.get('reporting') || ''} aria-label="What the agent reports"
              onChange={(e) => setParam('reporting', e.target.value)}>
              <option value="">Any report</option>
              <option value="not_reporting">Not reporting</option>
              <option value="unencrypted">Not encrypted</option>
              <option value="low_disk">Disk nearly full</option>
              <option value="needs_patching">Needs patching</option>
            </select>
            <select value={params.get('warranty') || ''} aria-label="Warranty"
              onChange={(e) => setParam('warranty', e.target.value)}>
              <option value="">Any warranty</option>
              <option value="expiring">Ending soon</option>
              <option value="expired">Expired</option>
              <option value="none">None recorded</option>
            </select>
            <select value={params.get('away') ? 'away' : params.get('no_default') ? 'none' : ''}
              aria-label="Default office filter"
              onChange={(e) => {
                const next = liveParams();
                next.delete('away'); next.delete('no_default'); next.delete('offset');
                if (e.target.value === 'away') next.set('away', '1');
                if (e.target.value === 'none') next.set('no_default', '1');
                setParams(next);
              }}>
              <option value="">Any default office</option>
              <option value="away">Away from default office</option>
              <option value="none">No default office</option>
            </select>
            {data?.label_filter && (
              <select value={params.get('label') || ''} aria-label="Label printing"
                onChange={(e) => setParam('label', e.target.value)}>
                <option value="">Any label</option>
                <option value="unprinted">Label not printed yet</option>
              </select>
            )}
            <label className="as-check">
              <input type="checkbox" checked={params.get('no_location') === '1'}
                onChange={(e) => setParam('no_location', e.target.checked ? '1' : '')} />
              No location
            </label>
            {!contractor && meta?.access?.any && (
              <label className="as-check">
                <input type="checkbox" data-testid="personal-filter" checked={personalOnly}
                  onChange={() => {
                    const next = liveParams();
                    next.delete('status'); next.delete('offset');
                    if (next.get('personal') === '1') next.delete('personal'); else next.set('personal', '1');
                    setParams(next);
                  }} />
                Personal items only
              </label>
            )}
          </div>
        )}
      </div>

      {hasCompanies && companyIds.length > 0 && (
        <div className="as-filter-chips" data-testid="company-chips">
          <span className="as-quiet" style={{ fontSize: '0.82rem' }}>Showing</span>
          {companyIds.map((id) => {
            const c = id === 'none' ? { name: 'No company' } : companyOf(id);
            if (!c) return null;
            return (
              <span key={id} className={`as-co${id === 'none' ? ' as-co-none' : ''}`} data-color={c.color || undefined}>
                {id !== 'none' && <span className="as-co-dot" aria-hidden="true" />}
                <span>{c.name}</span>
                <button type="button" aria-label={`Stop filtering by ${c.name}`}
                  onClick={() => chooseCompanies(companyIds.filter((x) => x !== id))}>×</button>
              </span>
            );
          })}
          <button type="button" className="btn btn-plain btn-small" onClick={() => chooseCompanies([])}>Show every company</button>
        </div>
      )}

      {/* 131: the person's personal marks, for an administrator, when the list is filtered to one person. */}
      {!contractor && meta?.access?.admin && /^[uc]:/.test(params.get('holder') || '') && (
        <PersonalMarks holder={params.get('holder')} meta={meta} />
      )}
      {error && <p className="as-error" role="alert">{error}</p>}
      {bulkNote && <p className="as-note" data-tone="info" role="status">{bulkNote}</p>}

      {data && (
        <div className="as-reg-results">
          <span>{`${total} device${total === 1 ? '' : 's'}`}</span>
          {total > 0 && (
            <button type="button" className="btn btn-plain btn-small" onClick={exportList}>Export the list</button>
          )}
        </div>
      )}

      {data && rows.length === 0 ? (
        <div className="empty-state">
          <p>{total === 0 && !params.toString()
            ? 'No devices yet.'
            : 'No devices match these filters.'}</p>
        </div>
      ) : (
        <div className="as-table-wrap">
          <table className="as-table as-reg-table">
            <thead>
              <tr>
                {canAdd && (
                  <th className="as-tick-col">
                    {/* On a phone the table is cards and this row is all
                        that is left of the head, so it says what it does. */}
                    <label className="as-tick-all">
                      <input type="checkbox" className="as-select" ref={headBox} checked={wholePage}
                        disabled={pageIds.length === 0} onChange={tickPage}
                        aria-label="Tick every device on this page" />
                      <span className="as-tick-all-word" aria-hidden="true">Tick this page</span>
                    </label>
                  </th>
                )}
                <th>Tag</th><th>Device</th>
                {hasCompanies && <th>Company</th>}
                <th>With</th><th>Where</th><th>Status</th><th>Warranty</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const w = warrantyInWords(a.warranty_end);
                return (
                  <tr key={a.id} onClick={() => navigate(`/assets/${a.id}`, { state: cameFrom('Assets') })} data-picked={picked.has(a.id) || undefined}>
                    {canAdd && (
                      <td className="as-tick-col" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="as-select" checked={picked.has(a.id)}
                          onChange={() => toggle(a.id)} aria-label={`Choose ${tagText(a)}`} />
                      </td>
                    )}
                    <td data-label="Tag">
                      <a href={`/assets/${a.id}`} className="as-tag"
                        onClick={(e) => { e.preventDefault(); navigate(`/assets/${a.id}`, { state: cameFrom('Assets') }); }}>
                        {a.asset_tag || (a.is_personal ? 'Personal' : 'No tag yet')}
                      </a>
                    </td>
                    <td data-label="Device">
                      {deviceName(a)}
                      <span className="as-sub">
                        {[a.category_name, a.serial_number, a.is_shared ? 'Shared' : null]
                          .filter(Boolean).join(' · ')}
                      </span>
                    </td>
                    {hasCompanies && (
                      <td data-label="Company">
                        <CompanyChip name={a.company_name} color={a.company_color} code={a.company_code} none="—" />
                      </td>
                    )}
                    <td data-label="With">{holderLabel(a) || <span className="as-quiet">Nobody</span>}</td>
                    <td data-label="Where">
                      {a.location_path || <span className="as-quiet">—</span>}
                      {a.default_location_path && a.default_location_id !== a.location_id && (
                        <span className="as-sub">{`Office: ${a.default_location_path}`}</span>
                      )}
                    </td>
                    <td data-label="Status">
                      <span className="as-chip" data-tone={lifecycleTone(a.lifecycle_status)}>
                        {LIFECYCLE_LABEL[a.lifecycle_status] || a.lifecycle_status}
                      </span>
                    </td>
                    <td data-label="Warranty">
                      <span className="as-chip" data-tone={w.tone}>{w.text}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Said aloud as the selection changes; always in the page so a screen
          reader hears the first tick too. */}
      <p className="sr-only" aria-live="polite" data-testid="selection-live">
        {chosen > 0 ? selectionWords : ''}
      </p>

      {canAdd && chosen > 0 && (
        <div className="as-selbar" role="region" aria-label="Selected devices" data-testid="selection-bar">
          <div className="as-selbar-head">
            <strong data-testid="selection-count">{selectionWords}</strong>
            {offerAll && (
              <button type="button" className="btn btn-plain btn-small" data-testid="select-all-matching"
                onClick={() => setAllMatching(true)}>
                {`Select all ${total} matching`}
              </button>
            )}
            {allMatching && (
              <button type="button" className="btn btn-plain btn-small" onClick={() => setAllMatching(false)}>
                {`Only the ${picked.size} ticked`}
              </button>
            )}
            <span className="as-spacer" />
            <button type="button" className="btn btn-plain btn-small" onClick={clearSelection}>Clear selection</button>
          </div>
          {allMatching && (
            <p className="as-selbar-note">
              {`Print labels covers all ${total} matching${total > LABEL_CAP ? ` - the first ${LABEL_CAP}, the most one print holds` : ` (at most ${LABEL_CAP} in one print)`}. `}
              {`The other actions change only the ${picked.size} ticked ${picked.size === 1 ? 'device' : 'devices'}.`}
            </p>
          )}
          <div className="as-selbar-actions">
            {mayPrint && (
              <button type="button" className="btn btn-primary btn-small" data-testid="print-labels" onClick={printLabels}>
                Print labels
              </button>
            )}
            {!contractor && picked.size > 0 && (
              <span className="as-selbar-group" data-testid="bulk-move">
                <label htmlFor="as-bulk-move" className="as-quiet">Move to</label>
                <select id="as-bulk-move" value={bulkPlace} onChange={(e) => setBulkPlace(e.target.value)}>
                  <option value="">Choose a place…</option>
                  {activePlaces.map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
                </select>
                <button type="button" className="btn btn-secondary btn-small" disabled={!bulkPlace} onClick={movePicked}>
                  Move
                </button>
              </span>
            )}
            {!contractor && picked.size > 0 && (
              <span className="as-selbar-group" data-testid="bulk-office">
                <label htmlFor="as-bulk-office" className="as-quiet">Set default office</label>
                <select id="as-bulk-office" value={bulkOffice} onChange={(e) => setBulkOffice(e.target.value)}>
                  <option value="">Choose…</option>
                  <option value="current">Use each one’s current place</option>
                  {activePlaces.map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
                </select>
                <button type="button" className="btn btn-secondary btn-small" disabled={!bulkOffice} onClick={setOfficeOnPicked}>
                  Apply office
                </button>
              </span>
            )}
            {hasCompanies && picked.size > 0 && (
              <span className="as-selbar-group" data-testid="bulk-company">
                <label htmlFor="as-bulk-company" className="as-quiet">Set company</label>
                <select id="as-bulk-company" value={bulkCompany} onChange={(e) => setBulkCompany(e.target.value)}>
                  <option value="">Choose…</option>
                  {liveCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  <option value="none">No company</option>
                </select>
                <button type="button" className="btn btn-secondary btn-small" disabled={!bulkCompany} onClick={setCompanyOnPicked}>
                  Apply
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {total > PAGE && (
        <div className="as-pager">
          <span>{`${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}</span>
          <span>
            <button type="button" className="btn btn-secondary btn-small" disabled={offset === 0}
              onClick={() => setParam('offset', String(Math.max(0, offset - PAGE)))}>Previous</button>{' '}
            <button type="button" className="btn btn-secondary btn-small" disabled={offset + PAGE >= total}
              onClick={() => setParam('offset', String(offset + PAGE))}>Next</button>
          </span>
        </div>
      )}

      {importing && (
        <AssetImport
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); refreshAssetMeta(); load(); }}
        />
      )}

      {adding && (
        <AssetForm
          meta={meta}
          onClose={() => setAdding(false)}
          onSaved={(saved, then) => {
            setAdding(false);
            refreshAssetMeta();
            if (then === 'print') navigate(...labelsFor(saved));
            else navigate(`/assets/${saved.id}`, { state: cameFrom('Assets') });
          }}
        />
      )}
    </div>
  );
}
